import { useEffect, useMemo, useRef } from 'react';
import { WidthProvider, Responsive } from 'react-grid-layout';

import { colorsArr } from '../../../utilities/constants/launchpadConstants';

import './LaunchpadGridLayout.scss';

interface Widget {
	id: string;
	coords?: { x: number; y: number };
	dims?: { w: number; h?: number };
	minW?: number;
	maxW?: number;
	theme?: string;
	filters?: any;
}

interface LaunchpadGridLayoutProps {
	widgets?: Widget[];
	editMode?: boolean;
	getWidgetById?: any;
	OnBreakpointChange?: (breakpoint: string) => void;
	snackbarRef?: React.MutableRefObject<number> | null;
	prevWidgetsRef?: React.MutableRefObject<Widget[]>;
	iseditable?: boolean;
	handleEditFunction?: () => void;
	handleClose?: () => void;
	setShowModal?: (show: boolean) => void;
	setShowConformationModal?: (show: boolean) => void;
	setIsRestoreDefault?: (restore: boolean) => void;
	setShowNoFeatureModal?: (show: boolean) => void;
	currentBreakpoint?: string;
	onWidthChange?: (width: number) => void;
	handleSaveLaunchpadConfig?: () => void;
}

const LaunchpadResponsiveGridLayout = ({
	widgets = [],
	editMode = false,
	getWidgetById = () => null,
	OnBreakpointChange = () => {},
	snackbarRef = null,
	prevWidgetsRef = { current: [] },
	iseditable = false,
	handleEditFunction = () => {},
	handleClose = () => {},
	setShowModal = () => {},
	setShowConformationModal = () => {},
	setIsRestoreDefault = () => {},
	setShowNoFeatureModal = () => {},
	currentBreakpoint = '',
	onWidthChange = () => {},
	handleSaveLaunchpadConfig = () => {},
}: LaunchpadGridLayoutProps) => {
	const gridRef = useRef<HTMLDivElement>(null);
	const ResponsiveGridLayout = useMemo(() => WidthProvider(Responsive), []);

	useEffect(() => {
		const hasUnsavedChanges = (prev: Widget[], current: Widget[]) => {
			if (!editMode) {
				return false;
			}
			const cleanPrev = removeFilterData(prev);
			const cleanCurrent = removeFilterData(current);
			return JSON.stringify(cleanPrev) !== JSON.stringify(cleanCurrent);
		};

		const removeFilterData = (widgetData: Widget[]) => {
			return widgetData.map((widget) => {
				if (widget.id === 'widget-events') {
					const { filters, dims, ...rest } = widget;
					const eventWidget = { ...{ dims: { w: dims?.w || 0 } }, ...rest };
					return eventWidget;
				}
				const { dims, ...rest } = widget;
				const widgetDetail = { ...{ dims: { w: dims?.w || 0 } }, ...rest };
				return widgetDetail;
			});
		};
		const isUnsavedChanges = hasUnsavedChanges(prevWidgetsRef.current, widgets);

		if (editMode && isUnsavedChanges) {
			if (snackbarRef?.current === 0) {
				snackbarRef.current = 1;
			}
		} else if (!editMode || !isUnsavedChanges) {
			if (snackbarRef) {
				snackbarRef.current = 0;
			}
		}

		const handleBeforeUnload = (event: BeforeUnloadEvent) => {
			if (editMode && isUnsavedChanges) {
				event.preventDefault();
			}
		};
		window.addEventListener('beforeunload', handleBeforeUnload);

		return () => {
			window.removeEventListener('beforeunload', handleBeforeUnload);
		};
	}, [editMode, widgets, snackbarRef, prevWidgetsRef]);

	return (
		<div
			ref={gridRef}
			className="bay-launcpad bayLaunchpad-responsive-grid-layout w-100 p-0 m-0"
		>
			<ResponsiveGridLayout
				autoSize
				breakpoints={{
					xxl: 1440,
					xl: 1145,
					lg: 992,
					md: 768,
					sm: 576,
					xs: 338,
					xxs: 290,
					xxxs: 250,
				}}
				className="layout"
				cols={{ xxl: 5, xl: 5, lg: 5, md: 2, sm: 1, xs: 1, xxs: 1, xxxs: 1 }}
				compactType="vertical"
				containerPadding={[0, 0]}
				isDraggable={false}
				isResizable={false}
				margin={[20, 20]}
				preventCollision={false}
				rowHeight={70}
				useCSSTransforms
				onBreakpointChange={OnBreakpointChange}
				onWidthChange={onWidthChange}
			>
				{widgets &&
					widgets?.map((widget) => {
						let widgetBgColor;
						if (widget?.theme) {
							const editModeBg =
								(colorsArr as any)[widget.theme].bgColor === 'bg-dark'
									? 'bg-900'
									: (colorsArr as any)[widget.theme].bgColor === 'bg-white'
										? 'bg-white'
										: `${(colorsArr as any)[widget.theme].bgColor}-300`;
							widgetBgColor = editMode
								? editModeBg
								: (colorsArr as any)[widget.theme].bgColor === 'bg-dark' ||
									  (colorsArr as any)[widget.theme].bgColor === 'bg-white'
									? (colorsArr as any)[widget.theme].bgColor
									: `${(colorsArr as any)[widget.theme].bgColor}-500`;
						}
						return (
							<div
								key={widget.id}
								className={`reactGridItem d-block ${['widget-websites', 'widget-header'].includes(widget?.id) ? '' : 'overflow-hidden'} rounded-2 ${widget?.theme ? widgetBgColor : 'bg-white'} ${widget?.id === 'widget-header-action' && 'border-0 '}`}
								data-grid={{
									x: widget?.coords?.x || 0,
									y: widget?.coords?.y || 0,
									w: widget?.dims?.w || 1,
									h: widget?.dims?.h !== undefined ? widget?.dims?.h : 1,
									i: widget.id,
									minW:
										parseFloat(String(widget?.minW || 0)) > parseFloat(String(widget?.dims?.w || 0))
											? parseFloat(String(widget?.dims?.w || 0))
											: parseFloat(String(widget?.minW || 0)) < parseFloat(String(widget?.dims?.w || 0))
												? Math.round(parseFloat(String(widget?.minW || 0)))
												: parseFloat(String(widget?.minW || 0))
													? Math.round(parseFloat(String(widget?.minW || 0)))
													: 1,
									maxW:
										parseFloat(String(widget?.maxW || 0)) > parseFloat(String(widget?.dims?.w || 0))
											? parseFloat(String(widget?.maxW || 0))
											: parseFloat(String(widget?.maxW || 0)) < parseFloat(String(widget?.dims?.w || 0))
												? Math.ceil(parseFloat(String(widget?.dims?.w || 0)))
												: parseFloat(String(widget?.maxW || 0)) < parseFloat(String(widget?.minW || 0))
													? Math.round(parseFloat(String(widget?.maxW || 0)))
													: parseFloat(String(widget?.minW || 0)),
									isDraggable: false,
								}}
								style={{
									border: !editMode ? '' : '2px dashed var(--bs-primary)',
								}}
							>
								{getWidgetById({
									widgetId: widget.id,
									editMode,
									iseditable,
									handleEditFunction,
									handleClose,
									setShowModal,
									setShowConformationModal,
									setIsRestoreDefault,
									setShowNoFeatureModal,
									currentBreakpoint,
									handleSaveLaunchpadConfig,
								})}
							</div>
						);
					})}
			</ResponsiveGridLayout>
		</div>
	);
};

export default LaunchpadResponsiveGridLayout;
